import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { BankAccountActions, RecipientsActions, TransactionsActions, WithdrawalsActions } from './wallet.actions';
import { WalletService } from './wallet.service';
import { walletFeature } from './wallet.selectors';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';

@Injectable()
export class WalletEffects {

  private actions$ = inject(Actions);
  private store$ = inject(Store);
  private walletService = inject(WalletService);
  private messageService = inject(MessageService);

  getBankAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BankAccountActions.getBankAccount),
      mergeMap(() =>
        this.walletService.getLandlordBankAccount().pipe(
          map((res) => {
            if (res.success) {
              return BankAccountActions.onGetBankAccount({
                status: 'success',
                bankAccount: res.data,
                message: '',
                httpStatus: res.status
              });
            }
            return BankAccountActions.onGetBankAccount({
              status: 'error',
              bankAccount: null,
              message: res.niceErrorMessage,
              httpStatus: res.status
            });
          })
        )
      )
    )
  );


  getTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionsActions.getTransactions),
      withLatestFrom(this.store$.select(walletFeature.selectTransactionFilters)),
      switchMap(([action, filters]) =>
        this.walletService.getTransactions(action.pageKey, action.size, filters).pipe(
          map((response) => {
              if (response.success) {
                return TransactionsActions.onGetTransactions({
                  status: 'success',
                  message: '',
                  items: response.data.items,
                  append: action.append,
                  pagination: {
                    nextPageKey: response.data.nextPageKey,
                    pageSize: response.data.pageSize,
                    numberOfItems: response.data.numberOfItems,
                    hasMore: response.data.hasMore
                  }
                });
              } else {
                return TransactionsActions.onGetTransactions({
                  status: 'error',
                  message: response.niceErrorMessage,
                  items: [],
                  pagination: null
                });
              }
            }
          )
        )
      )
    )
  );

  setTransactionFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionsActions.setTransactionFilters),
      filter(({ filters }) => {
        const keys = Object.keys(filters);
        return !(keys.length === 1 && keys[0] === 'page');
      }),
      map(() => TransactionsActions.getTransactions({}))
    )
  );


  setWithdrawalFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WithdrawalsActions.setWithdrawalFilters),
      map(() => WithdrawalsActions.getWithdrawals({}))
    )
  );

  clearTransactionFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionsActions.clearTransactionFilters),
      map(() => TransactionsActions.getTransactions({}))
    )
  );

  clearWithdrawalFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WithdrawalsActions.clearWithdrawalFilters),
      map(() => WithdrawalsActions.getWithdrawals({}))
    )
  );

  getWithdrawals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WithdrawalsActions.getWithdrawals),
      withLatestFrom(this.store$.select(walletFeature.selectWithdrawalFilters)),
      switchMap(([action, filters]) =>
        this.walletService.getWithdrawals(action.page, action.size, action.sort, filters).pipe(
          map((response) => {
              if (response.success) {
                return WithdrawalsActions.onGetWithdrawals({
                  status: 'success',
                  message: '',
                  items: response.data.items,
                  append: action.append,
                  pagination: {
                    pageNumber: response.data.pageNumber,
                    pageSize: response.data.pageSize,
                    numberOfItems: response.data.numberOfItems,
                    hasMore: response.data.hasMore
                  }
                });
              } else {
                return WithdrawalsActions.onGetWithdrawals({
                  status: 'error',
                  message: response.niceErrorMessage,
                  items: [],
                  pagination: null
                });
              }
            }
          )
        )
      )
    )
  );


  createWithdrawal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WithdrawalsActions.createWithdrawal),
      mergeMap((action) =>
        this.walletService.createWithdrawal(action).pipe(
          map((response) => {
            if (response.success) {
              return WithdrawalsActions.onCreateWithdrawal({
                status: 'success',
                message: '',
                withdrawal: response.data
              });
            }
            this.messageService.showSnackbar(response.niceErrorMessage, ToastSeverity.Error);
            return WithdrawalsActions.onCreateWithdrawal({
              status: 'error',
              message: response.niceErrorMessage,
              withdrawal: null
            });
          }))
      )
    )
  );

  refreshAfterWithdrawal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WithdrawalsActions.onCreateWithdrawal),
      switchMap((action) => {
        if (action.status === 'success') {
          return [
            BankAccountActions.getBankAccount(),
            WithdrawalsActions.getWithdrawals({})
          ];
        }
        return [];
      })
    )
  );
  getRecipients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecipientsActions.getRecipients),
      switchMap((action) =>
        this.walletService.getRecipients(action.page, action.size, action.sort).pipe(
          map((response) => {
              if (response.success) {
                return RecipientsActions.onGetRecipients({
                  status: 'success',
                  message: '',
                  items: response.data.items,
                  pagination: {
                    pageNumber: response.data.pageNumber,
                    pageSize: response.data.pageSize,
                    numberOfItems: response.data.numberOfItems,
                    hasMore: response.data.hasMore
                  }
                });
              }
              return RecipientsActions.onGetRecipients({
                status: 'error',
                message: response.niceErrorMessage,
                items: [],
                pagination: null
              });
            }
          )
        )
      )
    )
  );


  createRecipient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecipientsActions.createRecipient),
      mergeMap((action) =>
        this.walletService.createRecipient(action).pipe(
          map((response) => {
            if (response.success) {
              return RecipientsActions.onCreateRecipient({
                status: 'success',
                message: '',
                recipient: response.data
              });
            }
            return RecipientsActions.onCreateRecipient({
              status: 'error',
              message: response.niceErrorMessage,
              recipient: null
            });
          })
        )
      )
    )
  );

  updateRecipient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecipientsActions.updateRecipient),
      mergeMap((action) =>
        this.walletService.updateRecipient(action.id, action.nickname).pipe(
          map((response) => {
            if (response.success) {
              return RecipientsActions.onUpdateRecipient({
                recipient: response.data,
                status: 'success',
                message: ''
              });
            } else return RecipientsActions.onUpdateRecipient({
              recipient: null,
              status: 'error',
              message: response.niceErrorMessage
            });
          })
        )
      )
    )
  );

  deleteRecipient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecipientsActions.deleteRecipient),
      mergeMap((action) =>
        this.walletService.deleteRecipient(action.id).pipe(
          map((res) => {
            if (res.success) {
              return RecipientsActions.onDeleteRecipient({
                id: action.id,
                status: 'success',
                message: ''
              });
            }
            this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return RecipientsActions.onDeleteRecipient({
              id: action.id,
              status: 'error',
              message: res.niceErrorMessage
            });
          })
        )
      )
    )
  );
}
